import type { CodeSnippetWidgetData } from '@playbooks/action-cards-utils'
import type { FunctionComponent } from 'react'

import { useState } from 'react'
import type { WidgetProps } from './widgetTypes'
import { CustomButton } from '../../components/ui/CustomButton'
import { useTranslation } from '../../contexts/localization/Localization'

interface CodeSnippetWidgetProps extends WidgetProps {
  widget: CodeSnippetWidgetData
}

/**
 * Widget to execute a code snippet
 */
const CodeSnippetWidget: FunctionComponent<CodeSnippetWidgetProps> = ({ widget }) => {
  const { text: t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const functionName = `linkando_${widget.name}`
  const externalScriptName = `${widget.name}_${widget.id}`

  async function runCodeSnippet() {
    const widgetFunction = (window as any)[functionName]

    if (typeof widgetFunction === 'function') {
      // execute local function defined in window
      widgetFunction()
    } else {
      // fetch and execute remote script
      try {
        // @ts-ignore
        const response = await fetch(`${import.meta?.env?.VITE_BLOB_STORAGE_URL}/scripts/${externalScriptName}.js`)
        if (response.ok) {
          // add loaded script as window function
          const script = await response.text()
          ;(window as any)[functionName] = new Function(script)
          ;(window as any)[functionName]()
        } else {
          throw new Error(`Failed to load script: ${response.statusText}`)
        }
      } catch (e) {
        console.error('Remote script could not be executed', e)
        // dispatch event with a function name as a fallback
        ;(window as any)[functionName] = () => {
          console.log(`Dispatching event: ${functionName}`)
          const event = new CustomEvent(functionName)
          dispatchEvent(event)
        }
        ;(window as any)[functionName]()
      }
    }
  }

  async function handleButtonClick() {
    setIsLoading(true)
    try {
      await runCodeSnippet()
    } catch (error: any) {
      console.error(`Error while script execution: ${error}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <CustomButton
        size="lg"
        isLoading={isLoading}
        onClick={async () => {
          if (!isLoading) await handleButtonClick()
        }}
      >
        <>{widget.buttonLabel || t('codeSnippetWidget_defaultButton')}</>
      </CustomButton>
    </div>
  )
}

export default CodeSnippetWidget
